<template>
  <div ref="element" class="recipe-search">
    <div v-if="showFilters" class="field has-addons">
      <div class="control has-icons-left is-rounded is-flex-grow-1">
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
        <input
          ref="textInput"
          class="input is-rounded"
          type="search"
          :placeholder="searchPlaceholder"
          :value="recipeSearchStore.q"
          style="z-index: 3"
          inputmode="search"
          @input="setQueryFilter(textInput.value)"
        />
      </div>

      <div class="control">
        <button
          title="Advanced Filtering"
          class="button rounded-right"
          :class="{ 'is-active': showAdvanced, 'is-info': showAdvanced }"
          @click="showAdvanced = !showAdvanced"
        >
          <span class="icon is-left">
            <i class="fa-light fa-sliders-h"></i>
          </span>
          <span>Filters</span>
        </button>
      </div>
    </div>

    <div v-show="showAdvanced" class="box">
      <DateRange
        :begin="recipeSearchStore.beginDate"
        :end="recipeSearchStore.endDate"
        @update:model-value="setDateFilter"
      />

      <div class="is-flex is-align-items-center gap-3">
        <TagInput
          v-if="showTagInput"
          :model-value="recipeSearchStore.activeTags"
          :all-tags="recipeTagStore.allTags || []"
          :allow-new="false"
          placeholder="Filter by tag..."
          @update:model-value="setTagFilter"
          @advanced="(tag) => tagEditController.show(tag)"
        />

        <TagManager :tag-store="recipeTagStore" />
      </div>

      <TagEditDialog
        v-if="editingTag"
        :id="editingTag.id"
        :name="editingTag.name"
        :color="editingTag.color"
        :count="editingTag.count"
        @save="(tag) => tagEditController.save(tag)"
        @cancel="(tag) => tagEditController.cancel(tag)"
        @destroy="(tag) => tagEditController.destroy(tag)"
      />

      <div
        v-if="showRecipeTypeFilters"
        class="field is-flex-wrap-wrap is-horizontal pt-4"
        style="gap: 1em"
      >
        <TristateOption
          :model-value="recipeSearchStore.archived"
          caption="Archived Recipes"
          @update:model-value="(v) => recipeSearchStore.setRecipeTypeFilter('archived', v)"
        />
        <TristateOption
          :model-value="recipeSearchStore.subrecipe"
          caption="Subrecipes"
          @update:model-value="(v) => recipeSearchStore.setRecipeTypeFilter('subrecipe', v)"
        />
        <TristateOption
          :model-value="recipeSearchStore.empty"
          caption="Empty Recipes"
          @update:model-value="(v) => recipeSearchStore.setRecipeTypeFilter('empty', v)"
        />
      </div>

      <div class="field">
        <a @click.prevent="recipeSearchStore.resetSearch">
          <span class="icon-text">
            <span class="icon has-text-danger">
              <i class="fa fa-times"></i>
            </span>
            <span class="is-bold">Reset all filters</span>
          </span>
        </a>
      </div>
    </div>

    <div v-if="!showAdvanced && hasArchivedRecipes" class="filter-archived">
      <ArchivedOnlyCheckbox
        :model-value="recipeSearchStore.archived"
        @update:model-value="(v) => recipeSearchStore.setRecipeTypeFilter('archived', v)"
      />
    </div>

    <div
      v-if="selectionMode"
      class="is-fullwidth is-flex is-flex-direction-row is-justify-content-space-between row-underneath-recipe-searchbar"
      :class="alwaysShowModifiedColumn ? 'always-showing-column-headers' : ''"
    >
      <div class="is-flex has-flex-vcentered">
        <button
          v-if="clearSelectionEnabled"
          class="button is-small mr-3 is-info"
          style="border-radius: 3px"
          @click="handleClearSelection"
        >
          Clear Selection
        </button>
        <div v-if="recipeSearchStore.selection.length > 0" class="number-selected is-small-mobile">
          {{ numberFormat(recipeSearchStore.selection.length) }}
          <span class="is-hidden-mobile">recipes</span>
          selected
        </div>
      </div>
    </div>

    <component
      :is="listComponent"
      v-if="recipeSearchStore.loaded"
      :recipe-search-store="recipeSearchStore"
      :selection-mode="selectionMode"
      :multiple-selection-mode="multipleSelectionMode"
      :allow-inline-tag-edits="allowInlineTagEdits"
      :show-recipe-actions-column="showRecipeActionsColumn"
      :show-tags="showTags"
      :show-modified="showModified"
      :always-show-modified-column="alwaysShowModifiedColumn"
      :enable-archiving="enableArchiving"
      :allow-locking="allowLocking"
      :delete-confirms="deleteConfirms"
    />

    <PagerWidget
      v-if="recipeSearchStore.loaded"
      :model-value="recipeSearchStore.currentPage"
      :total-entries="recipeSearchStore.totalEntries"
      :per-page="recipeSearchStore.perPage"
      @update:model-value="changePage"
    />
  </div>
</template>

<script setup>import { computed as _computed, ref as _ref } from 'vue';

  import { onMounted, watch } from "vue";
  import { useEventListener } from "@vueuse/core";

  import ArchivedOnlyCheckbox from "./archived-only-checkbox.vue";
  import DateRange from "./date-range.vue";
  import TagInput from "./tag-input.vue";
  import RecipeList from "./recipe-list.vue";
  import PagerWidget from "./pager-widget.vue";
  import TristateOption from "./tristate-option.vue";
  import TagEditDialog from "./tag-edit-dialog.vue";
  import TagManager from "./tag-manager.vue";

  import { useTagEditController } from "./lib/tag-edit-controller.js";
  import { tagsWithUpdatedTag } from "./stores/tag-store.js";
  import { numberFormat } from "./stores/recipe-search-store.js";

  const props = defineProps({
    load: { type: Boolean, default: true },
    focus: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Search by name..." },
    userHasArchivedRecipes: { type: Boolean, default: true },

    showFilters: { type: Boolean, default: true },
    showRecipeTypeFilters: { type: Boolean, default: true },
    defaultShowAdvanced: { type: Boolean, default: false },

    /* Parent component passes in the stores. */
    recipeSearchStore: { type: Object, required: true },
    recipeTagStore: { type: Object, required: true },

    /* Optional props for recipe-list */
    selectionMode: { type: Boolean, default: false },
    multipleSelectionMode: { type: Boolean, default: (props) => props.selectionMode },

    enableArchiving: { type: Boolean, default: true },
    allowLocking: { type: Boolean, default: false },
    deleteConfirms: { type: Object, default: null },

    allowInlineTagEdits: { type: Boolean, default: true },
    showTags: { type: Boolean, default: true },

    showRecipeActionsColumn: { type: Boolean, default: true },

    showModified: { type: Boolean, default: true },
    alwaysShowModifiedColumn: { type: Boolean, default: false },

    /* Allow alternate list components */
    listComponent: { type: Object, default: () => RecipeList },
  });

  const hasArchivedRecipes = _computed(() => {
    return (
      props.userHasArchivedRecipes ||
      recipeSearchStore.value.selectionArchiveState.includes("archived") ||
      recipeSearchStore.value.knownArchivedRecipesExist
    );
  });

  const recipeSearchStore = _computed(() => props.recipeSearchStore);
  const recipeTagStore = _computed(() => props.recipeTagStore);

  const element = _ref(null);
  let textInput = _ref(null);

  let showAdvanced = _ref(props.defaultShowAdvanced && recipeSearchStore.value.hasAdvancedFilters);

  let editingTag = _ref(null);

  const tagEditController = useTagEditController({
    store: recipeTagStore.value,
    editingTag: (editingTag),
    tags: null,
  });

  const clearSelectionEnabled = _computed(() => recipeSearchStore.value.selection.length > 0);

  const showTagInput = _computed(() => {
    if (recipeSearchStore.value?.activeTags?.length > 0) {
      return true;
    }

    return props.showTags && recipeTagStore.value?.allTags?.length > 0;
  });

  const setQueryFilter = (searchString) => {
    if (searchString !== recipeSearchStore.value.q) {
      recipeSearchStore.value.setQueryFilter(searchString);
    }
  };
  const setTagFilter = (tags) => recipeSearchStore.value.setTagFilter(tags);

  const changePage = (newPage) => {
    recipeSearchStore.value.loadRecipePage(newPage);
    element.value.scrollIntoView({ behavior: "smooth" });
  };

  const setDateFilter = ({ begin, end }) => {
    recipeSearchStore.value.setDateFilter({ begin, end });
  };

  const handleClearSelection = () => {
    recipeSearchStore.value.clearSelection();
  };

  /* We don't want stale activeTags, so update them when a tag has been renamed */
  useEventListener(document, "recipe-tag:update", (ev) => {
    const { oldTag, newTag } = ev.detail;
    recipeSearchStore.value.activeTags = tagsWithUpdatedTag(recipeSearchStore.value.activeTags, oldTag, newTag);
  });

  watch(
    () => recipeSearchStore.value.hasAdvancedFilters,
    (hasAdvancedFilters) => {
      if (hasAdvancedFilters) {
        showAdvanced.value = true;
      }
    }
  );

  onMounted(async () => {
    if (props.load) {
      if (showTagInput.value) {
        recipeTagStore.value.loadAllTags();
      }

      if (!recipeSearchStore.value.loaded) {
        recipeSearchStore.value.loadInitial();
      }
    }

    if (props.focus) {
      textInput.value.focus();
    }
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  $spacer: 0.5rem;

  .recipe-search {
    display: flex;
    flex-direction: column;

    @include until($widescreen) {
      .field:first-child {
        margin-bottom: $spacer;
      }
    }
  }

  .row-underneath-recipe-searchbar {
    margin-top: -0.5 * $spacer;
    margin-bottom: $spacer;

    @include until($widescreen) {
      margin-top: $spacer;
    }
  }

  .filter-archived {
    position: relative;

    & > label {
      position: absolute;
      top: -0.5rem;
      right: 0;

      @include until($widescreen) {
        top: -0.25rem;
      }

      @include mobile {
        top: 0.625rem;
      }

      @media screen and (max-width: 320px) {
        top: 0.875rem;
      }
    }
  }

  .row-underneath-recipe-searchbar.always-showing-column-headers {
    @include until($tablet) {
      margin-bottom: calc($spacer / 2);
    }
  }

  button[disabled].is-info.is-outlined:not(:hover) {
    color: #75a7cf;
    border-color: #87b0d4;
  }

  .number-selected {
    @include until($desktop) {
      font-style: italic;
    }
  }
</style>
